/**
 * テーブル詳細モーダルの FK / SameEntity / Enum タグ表示用コンポーネント。
 * canEdit のときだけ X ボタンを表示し、確認ダイアログ経由で onDelete を呼ぶ。
 */
interface EdgeTagProps {
  /** タグの種類 (色分け) */
  variant: 'fk' | 'same' | 'enum' | 'shared';
  /** 表示テキスト */
  label: string;
  /** ホバー時の title 属性 */
  title?: string;
  /** 編集権限がある場合のみ X ボタンを出す */
  canEdit: boolean;
  /** クリック時 (タグ自体) のハンドラ。Enum 詳細ポップアップ起動などに使う */
  onClick?: () => void;
  /** X ボタン押下時のハンドラ。指定されていれば確認ダイアログ → 呼び出し */
  onDelete?: () => void;
  /** 削除進行中の disabled 表示用 */
  isDeleting?: boolean;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business カラム/テーブル行に表示する edge タグ。FK/SameEntity/Enum/共有テーブル種別ごとに色を出し分け、編集権限保持者には削除 X ボタンを併置
 * @graph-connects none
 */
export function EdgeTag(props: EdgeTagProps) {
  const colorMap = {
    fk: 'bg-[var(--color-info)]/10 text-[var(--color-info)]',
    same: 'bg-purple-500/10 text-purple-400',
    enum: 'bg-[var(--color-warning)]/10 text-[var(--color-warning)]',
    shared: 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20',
  } as const;
  const baseCls = `inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${colorMap[props.variant]}`;
  return (
    <span
      className={baseCls}
      title={props.title}
      onClick={(e) => {
        e.stopPropagation();
        if (props.onClick) props.onClick();
      }}
      role={props.onClick ? 'button' : undefined}
      style={props.onClick ? { cursor: 'pointer' } : undefined}
    >
      {props.label}
      {props.canEdit && props.onDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (props.onDelete && !props.isDeleting) {
              if (confirm(`「${props.label}」を削除します。よろしいですか?`)) {
                props.onDelete();
              }
            }
          }}
          disabled={props.isDeleting}
          className="ml-0.5 -mr-1 p-0.5 rounded hover:bg-black/20 transition-colors disabled:opacity-50"
          aria-label="削除"
        >
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      )}
    </span>
  );
}
